import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Screen } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/Button';
import { LinkButton } from '@/components/ui/LinkButton';
import { Card, EmptyState, Spinner, Banner } from '@/components/ui/Card';
import { useAppData } from '@/services/data/dataContext';
import { useAsync } from '@/hooks/useAsync';
import { pluralize } from '@/lib/text';

export function ShowsScreen() {
  const { data } = useAppData();
  const shows = useAsync(() => data.listShows(), [data]);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const remove = async (showId: string) => {
    setBusy(true);
    await data.deleteShow(showId);
    setBusy(false);
    setConfirming(null);
    shows.reload();
  };

  return (
    <>
      <TopBar
        title="Shows"
        action={
          <LinkButton to="/shows/new" size="sm">
            Add
          </LinkButton>
        }
      />
      <Screen>
        {shows.loading ? <Spinner label="Loading shows" /> : null}
        {shows.error ? <Banner tone="danger">{shows.error.message}</Banner> : null}

        {shows.data?.length === 0 ? (
          <EmptyState
            title="No shows yet"
            description="Paste a script or upload a file, then pick the characters you are playing."
            action={
              <LinkButton to="/shows/new" size="lg">
                Add your first show
              </LinkButton>
            }
          />
        ) : null}

        <ul className="flex flex-col gap-3">
          {shows.data?.map((show) => (
            <li key={show.showId}>
              <Card className="flex flex-col gap-2">
                <Link to={`/shows/${show.showId}`} className="block">
                  <p className="text-lg font-semibold">{show.title}</p>
                  <p className="text-ink-500 dark:text-ink-400 text-sm">
                    {show.myRoles.length > 0
                      ? `Playing ${show.myRoles.join(', ')}`
                      : 'No roles selected yet'}{' '}
                    · {show.myLineCount} {pluralize(show.myLineCount, 'line')} · {show.sceneCount}{' '}
                    {pluralize(show.sceneCount, 'scene')}
                  </p>
                </Link>

                {confirming === show.showId ? (
                  <div className="flex flex-col gap-2">
                    <p className="text-ink-500 dark:text-ink-400 text-sm">
                      Delete <strong>{show.title}</strong> and all of its progress?
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="danger"
                        block
                        disabled={busy}
                        onClick={() => void remove(show.showId)}
                      >
                        Delete permanently
                      </Button>
                      <Button size="sm" variant="secondary" block onClick={() => setConfirming(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirming(show.showId)}
                    aria-label={`Delete ${show.title}`}
                  >
                    Delete
                  </Button>
                )}
              </Card>
            </li>
          ))}
        </ul>
      </Screen>
    </>
  );
}
